/**
 * Finding one item among a few hundred by typing part of its name.
 *
 * The list is already loaded (the store screen has every item), so this is a
 * filter over it, not a request — instant, and it works when the connection
 * is slow. Picking fills the line; Escape or clicking away closes it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

export function ItemPicker({ items, onPick, placeholder = 'Type an item name…', autoFocus = false }) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const box = useRef(null);

  const matches = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return [];
    const words = needle.split(/\s+/);
    return items.filter((i) => words.every((w) => i.name.toLowerCase().includes(w))).slice(0, 12);
  }, [items, term]);

  useEffect(() => {
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const pick = (item) => {
    onPick(item);
    setTerm('');
    setOpen(false);
    setCursor(0);
  };

  return (
    <div ref={box} className="relative">
      <input
        value={term}
        autoFocus={autoFocus}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
          setCursor(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCursor((c) => Math.min(c + 1, matches.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCursor((c) => Math.max(c - 1, 0));
          } else if (e.key === 'Enter' && matches[cursor]) {
            e.preventDefault();
            pick(matches[cursor]);
          } else if (e.key === 'Escape') setOpen(false);
        }}
        placeholder={placeholder}
        aria-label="Find an item"
        className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
      />
      {open && matches.length > 0 && (
        <ul role="listbox" className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-lg border border-rule bg-surface shadow-lg">
          {matches.map((item, i) => (
            <li
              key={item.id}
              role="option"
              aria-selected={i === cursor}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(item);
              }}
              className={`flex cursor-pointer items-baseline justify-between gap-3 px-3 py-2 text-sm ${i === cursor ? 'bg-surface-3 text-ink' : 'text-ink-2'}`}
            >
              <span>
                {item.name}
                <span className="ml-2 text-xs text-ink-3">{item.category}</span>
              </span>
              <span className="shrink-0 text-xs text-ink-3">
                {item.packName} · store {item.store.packs}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** "1.5 × 4 kg tub" and "6,000 g": the two ways a quantity reads. */
export const packs = (v) => (Number.isInteger(v) ? String(v) : Number(v).toFixed(2).replace(/\.?0+$/, ''));
